import React from "react";
import { Typography, Tag, Tooltip, Button, Space, Popover } from "antd";
import Employee from "./tableItems/Employee";
import Patient from "../tables/Patient";
import AbsenceReason from "../tables/AbsenceReason";
import CustomTimePicker from "../util/CustomTimePicker";
import CustomMonthDaysSelection from "../util/CustomMonthDaysSelection";
import OverlappingEvents from "./OverlappingEvents";
import { FormattedMessage } from "react-intl";
import Date from "./tableItems/Date";
import moment from "moment";
import {
  CheckCircleOutlined,
  MedicineBoxTwoTone,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CustomerServiceOutlined,
} from "@ant-design/icons";
import { DeleteOutlined, CalendarOutlined } from "@ant-design/icons";
import renderTimewatchPopover from "./TimewatchPopover";
const { Text } = Typography;

const getIconColor = (record) => {
  const ok2goInfo = record?.ok2go_info;
  if (ok2goInfo) {
    if (ok2goInfo.date && ok2goInfo.exit_date) {
      return "#52c41a"; // Green
    } else if (!record.date || !record.duration_minutes) {
      return "#f5222d"; // Red
    } else {
      return "#faad14"; // Orange
    }
  }
  return "#faad14"; // Default orange
};

const getOk2goIcon = (record) => {
  return (
    record?.ok2go_info?.id && (
      <ClockCircleOutlined style={{ color: getIconColor(record) }} />
    )
  );
};

const getApprovalColumns = (
  quickEdit,
  intl,
  fromEmployee,
  conflict_events,
  onDateChange,
  onChange,
  disabledEndHours,
  disabledEndMinutes,
  disabledStartHours,
  disabledStartMinutes,
  onConfirm,
  holidays,
  religionId,
  onDeleteEvent,
  onShowEmployeeScheduling,
  timewatchEvents,
  employee_conflicts
) => {
  let tableItems = [];
  if (quickEdit) {
    tableItems = [
      {
        title: intl.formatMessage({
          id: fromEmployee
            ? "app.scheduling.table.patient"
            : "app.scheduling.table.employee",
        }),
        width: "180px",
        onCell: (_, index) => ({
          colSpan: 1,
        }),
        dataIndex: fromEmployee ? "order" : "employee",
        render(text, record) {
          return record.titleRow ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Text>{record.title}</Text>
            </div>
          ) : record.absenceRow ? (
            <AbsenceReason
              record={record}
              text={record.reason_id}
              settingsataName={"system_tables/absence_reasons"}
            />
          ) : fromEmployee ? (
            <div
              style={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Patient record={record.order.patient} tabindex="-1" />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                }}
              >
                {getOk2goIcon(record)}
                {conflict_events && conflict_events[record.id] && (
                  <Popover
                    placement="top"
                    title={null}
                    trigger="hover"
                    destroyTooltipOnHide
                    content={
                      <OverlappingEvents data={conflict_events[record.id]} />
                    }
                  >
                    <InfoCircleOutlined
                      style={{ color: "#faad14", marginLeft: "4px" }}
                    />
                  </Popover>
                )}
                {employee_conflicts &&
                  employee_conflicts[record.id] &&
                  employee_conflicts[record.id]?.length > 1 && (
                    <Popover
                      placement="top"
                      title={null}
                      trigger="hover"
                      destroyTooltipOnHide
                      content={
                        <OverlappingEvents
                          data={employee_conflicts[record.id]}
                          forbidden={true}
                        />
                      }
                    >
                      <InfoCircleOutlined
                        style={{ color: "#ff0000", marginLeft: "4px" }}
                      />
                    </Popover>
                  )}
                {renderTimewatchPopover({ record, timewatchEvents, holidays })}
              </div>
            </div>
          ) : record.is_day_center ? (
            <Text>
              {intl.formatMessage({
                id: "app.scheduling.day_center",
              })}
            </Text>
          ) : (
            <div
              style={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Employee record={record.employee} tabindex="-1" />
              </div>
              <div style={{ marginLeft: "8px" }}>{getOk2goIcon(record)}</div>
            </div>
          );
        },
        sorter: (a, b) =>
          fromEmployee
            ? a.order["patient"]?.surename.localeCompare(
                b.order["patient"]?.surename
              )
            : a.employee?.surename?.localeCompare(b.employee.surename),
        sortDirections: ["descend", "ascend"],
        fixed: "left",
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.date",
        }),
        width: "80px",
        dataIndex: "start",

        onCell: (_, index) => ({
          colSpan: 1,
        }),
        render(text, record) {
          return record.approved ||
            record.absenceRow ||
            record?.ok2go_info?.id ? (
            <Text style={{ padding: " 0 12px" }}>
              {moment(text).format("DD")}
            </Text>
          ) : (
            <CustomMonthDaysSelection
              date={text}
              placeholder={moment(text).format("DD")}
              onChange={(val) => onDateChange(val, record)}
              disablePicker={true}
            />
          );
        },
        sorter: (a, b) => a.start.valueOf() - b.start.valueOf(),
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "ascend",
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.dayInWeek",
        }),
        width: "70px",
        ellipsis: true,
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: 1,
        }),
        render(text, record) {
          return <Text>{moment.weekdays(moment(text).weekday())}</Text>;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.start",
        }),
        width: "100px",
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 11 : 1,
        }),
        render(text, record) {
          return record.approved && !record?.ok2go_info?.id ? (
            //regular approved events
            <Text style={{ padding: "0 12px" }}>
              {moment(text).format("HH:mm")}
            </Text>
          ) : record?.ok2go_info?.id ? (
            //ok2go events
            <Text style={{ padding: "0 12px" }}>
              {record?.ok2go_info?.date &&
                moment(record?.ok2go_info?.date).format("HH:mm")}
            </Text>
          ) : (
            !record.absenceRow && (
              <CustomTimePicker
                value={text && moment(text)}
                format={"HH:mm"}
                typingFormat={"HHmm"}
                onChange={(time) => onChange(time, record, "start")}
                onSelect={(time) => onChange(time, record, "start")}
                allowClear={false}
                disabled={record.approved}
                disabledHours={() => disabledStartHours(record)}
                disabledMinutes={(selectedHour) =>
                  disabledStartMinutes(selectedHour, record)
                }
                inputReadOnly={false}
                minuteStep={15}
                showNow={false}
                suffixIcon={null}
                disablePicker={true}
              />
            )
          );
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.end",
        }),
        width: "100px",
        dataIndex: "end",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.approved && !record?.ok2go_info?.id ? (
            //regular approved events
            <Text style={{ padding: "0 12px" }}>
              {moment(text).format("HH:mm")}
            </Text>
          ) : record?.ok2go_info?.id ? (
            //ok2go events
            <Text style={{ padding: "0 12px" }}>
              {record?.ok2go_info?.exit_date &&
                moment(record?.ok2go_info?.exit_date).format("HH:mm")}
            </Text>
          ) : (
            <CustomTimePicker
              value={text && moment(text)}
              format={"HH:mm"}
              typingFormat={"HHmm"}
              onChange={(time) => onChange(time, record, "end")}
              onSelect={(time) => onChange(time, record, "end")}
              disabledHours={() => disabledEndHours(record)}
              disabledMinutes={(selectedHour) =>
                disabledEndMinutes(selectedHour, record)
              }
              allowClear={false}
              disabled={record.approved}
              inputReadOnly={false}
              minuteStep={15}
              showNow={false}
              suffixIcon={null}
              disablePicker={true}
            />
          );
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.duration",
        }),
        width: "100px",
        dataIndex: "duration_minutes",
        render(text, record) {
          return <Text>{text && Math.round((text / 60) * 100) / 100} </Text>;
        },

        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.client",
        }),
        ellipsis: true,
        dataIndex: "client_name",
        render(text, record) {
          return <Text>{text}</Text>;
        },
        sorter: (a, b) => a.client_name.localeCompare(b.client_name),
        sortDirections: ["descend", "ascend"],
        width: "190px",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.holiday",
        }),
        width: "60px",
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          const holiday =
            holidays &&
            holidays.filter(
              (i) =>
                moment(i.date) >= moment(text).startOf("day") &&
                moment(i.date) <= moment(text).endOf("day") &&
                i.religion_id === religionId
            )[0];
          const holidayText =
            holiday !== undefined
              ? holiday.holiday_evening
                ? "ע.חג"
                : "חג"
              : "";
          return <Text>{holidayText}</Text>;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.hospitalized",
        }),
        width: "80px",
        dataIndex: "hospitalized",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return (
            text &&
            record.hospitalization && (
              <Tooltip
                title={`${moment(record.hospitalization.effective_from).format(
                  "DD/MM/YYYY"
                )}-${moment(record.hospitalization.effective_to).format(
                  "DD/MM/YYYY"
                )} (${
                  record.hospitalization.hospitalization_institute &&
                  record.hospitalization.hospitalization_institute.name
                })`}
              >
                <MedicineBoxTwoTone />
              </Tooltip>
            )
          );
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.approved_by",
        }),
        ellipsis: true,
        dataIndex: "approved_by",
        width: "120px",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return <Text>{text ? text["name"] : ""}</Text>;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.approved_at",
        }),
        width: "120px",
        ellipsis: true,
        dataIndex: "approved_at",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.approved && <Date text={text} format={"L"} />;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.status",
        }),
        dataIndex: "approved",
        width: "80px",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.prospectus_serial_number ? (
            <Tooltip
              title={intl.formatMessage(
                { id: "app.scheduling.table.prospectus_serial" },
                { prospectus_serial: record.prospectus_serial_number }
              )}
            >
              <Tag color="processing">
                {intl.formatMessage({
                  id: "app.scheduling.table.invoiced",
                })}
              </Tag>
            </Tooltip>
          ) : record.approved ? (
            <Tag color="success">
              {intl.formatMessage({
                id: "app.scheduling.table.verified",
              })}
            </Tag>
          ) : null;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.salary_approved",
        }),
        dataIndex: "salary_approved",
        width: "80px",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.salary_approved ? (
            <CheckCircleOutlined style={{ color: "#56C41A" }} />
          ) : null;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.house_visits.table.actions",
        }),
        key: "action",
        width: "100px",
        fixed: "right",
        onCell: (_, index) => ({
          colSpan: _.absenceRow ? 0 : 1,
        }),
        render: (text, record) =>
          !record.is_day_center &&
          !record.titleRow &&
          !record.absenceRow && (
            <Space size="middle">
              {!record?.ok2go_info?.id && (
                <Tooltip
                  title={
                    <FormattedMessage id="app.scheduling.table.deleteTooltip" />
                  }
                >
                  <Button
                    disabled={record.approved}
                    onClick={() => onConfirm(record)}
                    type="text"
                    icon={<DeleteOutlined />}
                    tabindex="-1"
                  />
                </Tooltip>
              )}
              <Tooltip
                title={
                  <FormattedMessage id="app.scheduling.table.showEmployeeScheduling" />
                }
              >
                <Button
                  onClick={() => onShowEmployeeScheduling(record)}
                  type="text"
                  icon={<CalendarOutlined />}
                  tabindex="-1"
                />
              </Tooltip>
            </Space>
          ),
      },
    ];
  } else {
    tableItems = [
      {
        title: intl.formatMessage({
          id: fromEmployee
            ? "app.scheduling.table.patient"
            : "app.scheduling.table.employee",
        }),
        width: "200px",
        fixed: "left",
        dataIndex: fromEmployee ? "order" : "employee",
        render(text, record) {
          return record.titleRow ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Text>{record.title}</Text>
            </div>
          ) : record.absenceRow ? (
            <AbsenceReason
              record={record}
              text={record.reason_id}
              settingsataName={"system_tables/absence_reasons"}
            />
          ) : fromEmployee ? (
            <div
              style={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Patient record={record.order.patient} tabindex="-1" />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                }}
              >
                {getOk2goIcon(record)}
                {conflict_events && conflict_events[record.id] && (
                  <Popover
                    placement="top"
                    title={null}
                    trigger="hover"
                    destroyTooltipOnHide
                    content={
                      <OverlappingEvents data={conflict_events[record.id]} />
                    }
                  >
                    <InfoCircleOutlined
                      style={{ color: "#faad14", marginLeft: "4px" }}
                    />
                  </Popover>
                )}
                {employee_conflicts &&
                  employee_conflicts[record.id] &&
                  employee_conflicts[record.id]?.length > 1 && (
                    <Popover
                      placement="top"
                      title={null}
                      trigger="hover"
                      destroyTooltipOnHide
                      content={
                        <OverlappingEvents
                          data={employee_conflicts[record.id]}
                          forbidden={true}
                        />
                      }
                    >
                      <InfoCircleOutlined
                        style={{ color: "#ff0000", marginLeft: "4px" }}
                      />
                    </Popover>
                  )}
                {renderTimewatchPopover({ record, timewatchEvents, holidays })}
              </div>
            </div>
          ) : record.is_day_center ? (
            <Text>
              {intl.formatMessage({
                id: "app.scheduling.day_center",
              })}
            </Text>
          ) : (
            <div
              style={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Employee record={record.employee} tabindex="-1" />
              </div>
              <div style={{ marginLeft: "8px" }}>{getOk2goIcon(record)}</div>
            </div>
          );
        },
        sorter: (a, b) =>
          fromEmployee
            ? a.order["patient"]?.surename?.localeCompare(
                b.order["patient"].surename
              )
            : a.employee?.surename?.localeCompare(b.employee.surename),
        sortDirections: ["descend", "ascend"],
        // defaultSortOrder: "ascend",
        onCell: (_, index) => ({
          colSpan: _.titleRow ? 13 : 1,
        }),
        fixed: "left",
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.date",
        }),
        width: "80px",
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: _.titleRow ? 0 : 1,
        }),
        render(text, record) {
          return record.approved ||
            record.absenceRow ||
            record?.ok2go_info?.id ? (
            <Text style={{ padding: " 0 12px" }}>
              {moment(text).format("DD")}
            </Text>
          ) : (
            <CustomMonthDaysSelection
              date={text}
              placeholder={moment(text).format("DD")}
              onChange={(val) => onDateChange(val, record)}
            />
          );
        },
        sorter: (a, b) => a.start.valueOf() - b.start.valueOf(),
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "ascend",
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.dayInWeek",
        }),
        width: "70px",
        ellipsis: true,
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: _.titleRow ? 0 : 1,
        }),
        render(text, record) {
          return <Text>{moment.weekdays(moment(text).weekday())}</Text>;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.start",
        }),
        width: "100px",
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: _.titleRow ? 0 : _.absenceRow ? 10 : 1,
        }),

        render(text, record) {
          return record.approved && !record?.ok2go_info?.id ? (
            //regular approved events
            <Text style={{ padding: "0 12px" }}>
              {moment(text).format("HH:mm")}
            </Text>
          ) : record?.ok2go_info?.id ? (
            //ok2go events
            <Text style={{ padding: "0 12px" }}>
              {record?.ok2go_info?.date &&
                moment(record?.ok2go_info?.date).format("HH:mm")}
            </Text>
          ) : (
            !record.absenceRow && (
              <CustomTimePicker
                value={moment(text)}
                format={"HH:mm"}
                typingFormat={"HHmm"}
                onChange={(time) => onChange(time, record, "start")}
                onSelect={(time) => onChange(time, record, "start")}
                disabledHours={() => disabledStartHours(record)}
                disabledMinutes={(selectedHour) =>
                  disabledStartMinutes(selectedHour, record)
                }
                allowClear={false}
                disabled={record.approved}
                inputReadOnly={false}
                minuteStep={15}
                showNow={false}
                suffixIcon={null}
              />
            )
          );
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.end",
        }),
        width: "100px",
        dataIndex: "end",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.approved && !record?.ok2go_info?.id ? (
            //regular approved events
            <Text style={{ padding: "0 12px" }}>
              {moment(text).format("HH:mm")}
            </Text>
          ) : record?.ok2go_info?.id ? (
            //ok2go events
            <Text style={{ padding: "0 12px" }}>
              {record?.ok2go_info?.exit_date &&
                moment(record?.ok2go_info?.exit_date).format("HH:mm")}
            </Text>
          ) : (
            <CustomTimePicker
              value={text && moment(text)}
              format={"HH:mm"}
              typingFormat={"HHmm"}
              onChange={(time) => onChange(time, record, "end")}
              onSelect={(time) => onChange(time, record, "end")}
              disabledHours={() => disabledEndHours(record)}
              disabledMinutes={(selectedHour) =>
                disabledEndMinutes(selectedHour, record)
              }
              allowClear={false}
              disabled={record.approved}
              inputReadOnly={false}
              minuteStep={15}
              showNow={false}
              suffixIcon={null}
            />
          );
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.duration",
        }),
        width: "100px",
        dataIndex: "duration_minutes",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>{text && Math.round((text / 60) * 100) / 100} </Text>
              {record.oncall && (
                <Tooltip
                  title={intl.formatMessage({
                    id: "app.scheduling.table.oncall_hours",
                  })}
                >
                  <CustomerServiceOutlined />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.client",
        }),
        ellipsis: true,
        dataIndex: "client_name",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return <Text>{text}</Text>;
        },
        sorter: (a, b) => a.client_name.localeCompare(b.client_name),
        sortDirections: ["descend", "ascend"],
        width: "140px",
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.holiday",
        }),
        width: "60px",
        dataIndex: "start",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          const holiday =
            holidays &&
            holidays.filter(
              (i) =>
                moment(i.date) >= moment(text).startOf("day") &&
                moment(i.date) <= moment(text).endOf("day") &&
                i.religion_id === religionId
            )[0];
          const holidayText =
            holiday !== undefined
              ? holiday.holiday_evening
                ? "ע.חג"
                : "חג"
              : "";
          return <Text>{holidayText}</Text>;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.hospitalized",
        }),
        width: "80px",
        dataIndex: "hospitalized",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return (
            text &&
            record.hospitalization && (
              <Tooltip
                title={`${moment(record.hospitalization.effective_from).format(
                  "DD/MM/YYYY"
                )}-${moment(record.hospitalization.effective_to).format(
                  "DD/MM/YYYY"
                )} (${
                  record.hospitalization.hospitalization_institute &&
                  record.hospitalization.hospitalization_institute.name
                })`}
              >
                <MedicineBoxTwoTone />
              </Tooltip>
            )
          );
        },
      },

      {
        title: intl.formatMessage({
          id: "app.scheduling.table.approved_by",
        }),
        ellipsis: true,
        dataIndex: "approved_by",
        width: "120px",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return <Text>{text ? text["name"] : ""}</Text>;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.approved_at",
        }),
        width: "120px",
        ellipsis: true,
        dataIndex: "approved_at",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.approved && <Date text={text} format={"L"} />;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.status",
        }),
        dataIndex: "approved",
        width: "80px",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.prospectus_serial_number ? (
            <Tooltip
              title={intl.formatMessage(
                { id: "app.scheduling.table.prospectus_serial" },
                { prospectus_serial: record.prospectus_serial_number }
              )}
            >
              <Tag color="processing">
                {intl.formatMessage({
                  id: "app.scheduling.table.invoiced",
                })}
              </Tag>
            </Tooltip>
          ) : record.approved ? (
            <Tag color="success">
              {intl.formatMessage({
                id: "app.scheduling.table.verified",
              })}
            </Tag>
          ) : null;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.scheduling.table.salary_approved",
        }),
        dataIndex: "salary_approved",
        width: "80px",
        onCell: (_, index) => ({
          colSpan: _.titleRow || _.absenceRow ? 0 : 1,
        }),
        render(text, record) {
          return record.salary_approved ? (
            <CheckCircleOutlined style={{ color: "#56C41A" }} />
          ) : null;
        },
      },
      {
        title: intl.formatMessage({
          id: "app.house_visits.table.actions",
        }),
        key: "action",
        width: "100px",
        fixed: "right",
        render: (text, record) =>
          !record.is_day_center &&
          !record.titleRow &&
          !record.absenceRow && (
            <Space size="middle">
              {!record?.ok2go_info?.id && (
                <Tooltip
                  title={
                    <FormattedMessage id="app.scheduling.table.deleteTooltip" />
                  }
                >
                  <Button
                    disabled={record.approved}
                    onClick={() => onDeleteEvent(record)}
                    type="text"
                    icon={<DeleteOutlined />}
                    tabindex="-1"
                  />
                </Tooltip>
              )}
              <Tooltip
                title={
                  <FormattedMessage id="app.scheduling.table.showEmployeeScheduling" />
                }
              >
                <Button
                  onClick={() => onShowEmployeeScheduling(record)}
                  type="text"
                  icon={<CalendarOutlined />}
                  tabindex="-1"
                />
              </Tooltip>
            </Space>
          ),
      },
    ];
  }

  return tableItems;
};

export { getApprovalColumns };
